import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import type { CookiePayload } from '@core/repositories/CookieRepository';

export const useCookies = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.cookies(workspaceId ?? ''),
    queryFn: () => container.cookies.list(workspaceId as string),
    enabled: Boolean(workspaceId),
  });
};

export const useCreateCookie = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CookiePayload) => container.cookies.create(workspaceId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.cookies(workspaceId) }),
  });
};

export const useUpdateCookie = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CookiePayload }) =>
      container.cookies.update(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.cookies(workspaceId) }),
  });
};

export const useDeleteCookie = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.cookies.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.cookies(workspaceId) }),
  });
};

export const useClearCookies = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => container.cookies.clear(workspaceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.cookies(workspaceId) }),
  });
};
